// withAuth.tsx
// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { useState, useEffect, useCallback, useRef, ComponentType } from "react";
import {
  XSM_APPLICATION,
  XSM_BASE_URL,
  USER_DOMAIN,
  XSM_ORIGIN,
  CLIENT_SECRET,
  CLIENT_URL,
} from "../constants/auth";
import "react-toastify/dist/ReactToastify.css";
import { concatAndEncodeBase64 } from "src/utils";
import { useAuth } from "src/hooks/useLogin";

// Calculate thresholds based on environment
const refreshThreshold = 5 * 60 * 1000; // 5 minutes
const checkInterval = 30000; // 30 seconds
// Define the HOC type
const withAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  return function WithAuth(props: P) {
    const { handleLogout } = useAuth();
    const [isActive, setIsActive] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const ACCESS_TOKEN = localStorage.getItem("dm_access_token") ?? "";
    const REFRESH_TOKEN = localStorage.getItem("dm_refresh_token") ?? "";

    const refreshTokenRef = useRef(REFRESH_TOKEN || "");
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const refreshAccessToken = useCallback(async () => {
      if (isRefreshing) return;
      setIsRefreshing(true);

      //base64 authorization
      const basicAuthorozationSecret = concatAndEncodeBase64(
        CLIENT_URL,
        CLIENT_SECRET
      );

      try {
        const response = await fetch(
          `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/token`,
          {
            method: "POST",
            headers: {
              Authorization: `Basic ${basicAuthorozationSecret}`,
              "Content-Type": "application/x-www-form-urlencoded",
              "xsm-application": XSM_APPLICATION,
              "Xsm-Domain": USER_DOMAIN,
              "Xsm-iscompressed": "false",
              "X-Original-Origin": XSM_ORIGIN,
            },
            body: new URLSearchParams({
              grant_type: "refresh_token",
              refresh_token: refreshTokenRef.current,
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to refresh access token");

        const data = await response.json();

        // For local testing, override expires_in with our test value
        const expiresInSeconds = data.expires_in;
        const expiresInMs = expiresInSeconds * 1000;
        const expirationTime = Date.now() + expiresInMs;
        console.log("Data =>", data);
        localStorage.setItem("dm_access_token", data.access_token);
        localStorage.setItem("dm_refresh_token", data.refresh_token);
        localStorage.setItem("dm_expires_in", expirationTime.toString());
        localStorage.setItem("dm_id_token", data?.id_token);
        refreshTokenRef.current = data.refresh_token;
      } catch (error) {
        handleLogout();
      } finally {
        setIsRefreshing(false);
      }
    }, [isRefreshing, handleLogout]);

    const checkTokenStatus = useCallback(() => {
      const storedExpiry = Number(localStorage.getItem("dm_expires_in"));
      if (!ACCESS_TOKEN || !storedExpiry) {
        handleLogout();
        return;
      }

      const currentTime = Date.now();
      const timeLeft = storedExpiry - currentTime;

      // Format time for better debugging
      const minutesLeft = Math.floor(timeLeft / 60000);
      const secondsLeft = Math.floor((timeLeft % 60000) / 1000);

      console.log(`Token status - ${minutesLeft}m ${secondsLeft}s remaining`, {
        expiresAt: new Date(storedExpiry).toLocaleTimeString(),
        currentTime: new Date(currentTime).toLocaleTimeString(),
      });

      if (timeLeft <= 0) {
        console.log("Token expired - logging out");
        handleLogout();
      } else if (timeLeft <= refreshThreshold && isActive) {
        console.log(
          `Token about to expire (${minutesLeft}m ${secondsLeft}s left) - refreshing...`
        );
        refreshAccessToken();
      }
    }, [
      isActive,
      ACCESS_TOKEN,
      refreshAccessToken,
      handleLogout,
      refreshThreshold,
    ]);

    useEffect(() => {
      if (ACCESS_TOKEN) {
        // Initial check
        checkTokenStatus();

        // Set up periodic checking
        intervalRef.current = setInterval(checkTokenStatus, checkInterval);
      }

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [ACCESS_TOKEN, checkTokenStatus, checkInterval]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        const isNowActive = document.visibilityState === "visible";
        setIsActive(isNowActive);
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, []);

    if (!ACCESS_TOKEN) {
      return null; // or redirect to login
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuth;
